// Oracle Hierarchical query helpers (CONNECT BY / START WITH) — surfaces
// the four hierarchical pseudocolumns / functions when the cursor's
// statement contains `CONNECT BY` or `START WITH`:
//
//   • `LEVEL`             — pseudocolumn returning the depth in the tree.
//   • `CONNECT_BY_ISLEAF` — pseudocolumn `1` if leaf row, `0` otherwise.
//   • `CONNECT_BY_ROOT`   — unary operator returning the root row's value.
//   • `SYS_CONNECT_BY_PATH` — function returning the path from root.
//
// Pure module — no Monaco / electron imports. Source-text scan only.

import { tokenize } from '../context/tokenizer';

const ENGINE = 'oracle' as const;

export type OracleHierarchicalCandidate = {
  readonly label: string;
  readonly insertText: string;
  readonly isSnippet: boolean;
  readonly detail: string;
  readonly triggerSignatureHelp?: boolean;
};

const HIERARCHICAL_CANDIDATES: readonly OracleHierarchicalCandidate[] = [
  {
    label: 'LEVEL',
    insertText: 'LEVEL',
    isSnippet: false,
    detail: 'Hierarchical pseudocolumn',
  },
  {
    label: 'CONNECT_BY_ISLEAF',
    insertText: 'CONNECT_BY_ISLEAF',
    isSnippet: false,
    detail: 'Hierarchical pseudocolumn',
  },
  {
    label: 'CONNECT_BY_ROOT',
    insertText: 'CONNECT_BY_ROOT ',
    isSnippet: false,
    detail: 'Hierarchical operator',
  },
  {
    label: 'SYS_CONNECT_BY_PATH',
    insertText: 'SYS_CONNECT_BY_PATH($0)',
    isSnippet: true,
    detail: 'Hierarchical function',
    triggerSignatureHelp: true,
  },
];

export function getOracleHierarchicalCandidates(): readonly OracleHierarchicalCandidate[] {
  return HIERARCHICAL_CANDIDATES;
}

/**
 * Returns true when the statement bounded by `[statementStart, statementEnd)`
 * is a hierarchical SELECT — i.e. one that legitimately uses the four
 * pseudocolumns above. Detection rules:
 *
 *   • `CONNECT BY` anywhere inside the statement is unambiguous → true.
 *   • `START WITH` is **context-ambiguous** in Oracle: it appears in
 *     `CREATE SEQUENCE … START WITH n`, `ALTER SEQUENCE … START WITH n`,
 *     and materialized-view `REFRESH … START WITH date` — none of which
 *     are hierarchical queries. To disambiguate, `START WITH` only
 *     triggers when a `FROM` keyword precedes it within the same
 *     statement (a hierarchical `START WITH` always follows a SELECT's
 *     FROM clause, whereas the DDL forms above have no FROM).
 *
 * The token scan masks strings and comments via the shared tokenizer, so
 * `'CONNECT BY'` inside a literal does not trigger a false positive.
 */
export function isInConnectByContext(
  source: string,
  statementStart: number,
  statementEnd: number,
): boolean {
  if (statementEnd <= statementStart) return false;
  const toks = tokenize(source, statementStart, statementEnd, ENGINE);
  let sawFrom = false;
  for (let i = 0; i < toks.length - 1; i++) {
    const a = toks[i]!;
    const b = toks[i + 1]!;
    if (a.type !== 'kw' && a.type !== 'ident') continue;
    const aUp = a.text.toUpperCase();
    // Track whether a FROM keyword has appeared earlier in this
    // statement — required to disambiguate hierarchical `START WITH`
    // from DDL `START WITH` (CREATE/ALTER SEQUENCE, materialized-view
    // REFRESH options), which never have a FROM clause.
    if (aUp === 'FROM') sawFrom = true;
    if (b.type !== 'kw' && b.type !== 'ident') continue;
    const bUp = b.text.toUpperCase();
    // CONNECT BY is unambiguous — only appears in hierarchical SELECTs.
    if (aUp === 'CONNECT' && bUp === 'BY') return true;
    // START WITH only counts when we've already seen a FROM in this
    // statement (i.e. we're definitely inside a SELECT, not a DDL).
    if (aUp === 'START' && bUp === 'WITH' && sawFrom) return true;
  }
  return false;
}
